import { Field, isString, useFormikContext } from 'formik'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useDocuments } from '@pages/Profile/DocumentManagement/DocumentManagementPage'

import { DocumentFileUpload } from '@ui/components/FileUpload/DocumentFileUpload'
import { DoubleSidedDocumentFileUploads } from '@ui/components/FileUpload/DoubleSidedDocumentFileUploads'
import { FileData } from '@ui/components/FileUpload/types'

import { useCategorySizesReadContext } from '@utils/CategorySizesContext'
import { isDoubleSidedDocument } from '@utils/uploadDocument'

interface DocumentFileUploadsFactoryProps {
  files?: FileData[]
  documentTypeId: number | string
  documentCategory: number
  onChange(fileData: FileData[]): void
  onBeforeRemove?(file: FileData): void
}

interface DocumentManagementDocument {
  filename: string
  error: boolean
}

export const DocumentFileUploadsFactory: React.FC<DocumentFileUploadsFactoryProps> = (props) => {
  const { documentTypeId, documentCategory, files = [], onChange, onBeforeRemove } = props

  const documentType = getDocumentType(documentTypeId)
  const isDoubleSided = isDoubleSidedDocument(documentCategory, documentType)

  const { setFieldValue } = useFormikContext()
  const { t } = useTranslation()

  const documentsProps = useDocuments()

  const memoizedDocuments = useMemo(() => documentsProps.data, [documentsProps.data])

  const { categorySizes } = useCategorySizesReadContext()

  useEffect(() => {
    setFieldValue('files', [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentTypeId])

  return (
    <>
      {isDoubleSided ? (
        <Field name='documents' validate={validateDocuments(t('Validation.Required'))}>
          {() => (
            <DoubleSidedDocumentFileUploads
              files={files}
              documentCategory={documentCategory}
              onUpload={onChange}
              onBeforeRemove={onBeforeRemove}
              documents={memoizedDocuments}
              categorySizes={categorySizes}
            />
          )}
        </Field>
      ) : (
        <DocumentFileUpload
          multiple
          files={files}
          documentCategory={documentCategory}
          onUpload={onChange}
          onBeforeRemove={onBeforeRemove}
          documents={memoizedDocuments}
          categorySizes={categorySizes}
        />
      )}
    </>
  )
}

const validateDocuments = (message: string) => (value: DocumentManagementDocument[]) => {
  const documents = value.filter((x) => !!x?.filename).filter((x) => !x.error)

  if (documents.length <= 1) {
    return message
  }

  return undefined
}

const getDocumentType = (documentTypeId: string | number) => {
  return isString(documentTypeId) ? Number.parseInt(documentTypeId, 10) : documentTypeId
}
